// useSSEChat.ts
import { useCallback, useEffect, useRef, useState } from "react";
import { apiClient } from "@/api";

export function useSSEChat(endpoint: string, onResponseComplete?: () => void) {
  const [text, setText] = useState("");
  const [status, setStatus] = useState<"idle"|"connecting"|"open"|"closed"|"error">("idle");
  const controllerRef = useRef<AbortController | null>(null);

  const sendMessage = useCallback(async (prompt: string) => {
    if (!prompt || status === "connecting" || status === "open") {
      return;
    }

    // Clean up any existing connection
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }

    setText("");
    setStatus("connecting");

    // ensure the token is fresh before starting the stream
    const tokenResult = await apiClient.ensureTokenIsFresh();
    console.log("Token refreshed:", tokenResult);

    const endpointWithQuery = `${endpoint}?prompt=${encodeURIComponent(prompt)}`;

    try {
      const controller = await apiClient.stream(
        endpointWithQuery,
        (message, controller) => {
          try {
            const { type, data } = JSON.parse(message.data);
            
            if (type === "token") {
              setText(t => t + data);
            } else if (type === "done") {
              setStatus("closed");
              onResponseComplete?.();
              controller.abort();
            } else if (type === "error") {
              setStatus("error");
              controller.abort();
            }
          } catch {
            // ignore malformed frames
          }
        },
        (error) => {
          console.error("SSE Chat error:", error);
          setStatus("error");
        },
        async (response) => {
          setStatus("open");
        }
      );
      
      controllerRef.current = controller;
    } catch (error) {
      console.error("Failed to start SSE stream:", error);
      setStatus("error");
    }
  }, [endpoint, onResponseComplete]);

  const abort = useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
    setStatus("closed");
  }, []);

  const reset = useCallback(() => {
    abort();
    setText("");
    setStatus("idle");
  }, [abort]);

  return { 
    text, 
    status, 
    sendMessage, 
    abort,
    reset
  };
}
